import React, { useEffect, useState, useRef } from 'react';
import TopNavbar from '../../../components/TopNavbar';
import SideNavbar from '../../../components/SideNavbar';
import { collection, query, where, getDocs, doc, updateDoc, getDoc, Timestamp, DocumentData } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { useAuth } from '../../../AuthContext';
import TableSkeleton from '../../../components/TableSkeleton';

// redux
import { useAppSelector } from "../../../redux/store";
import getCurrentScreen from '../../../Helper/GetScreen';

interface Lead {
  name: string;
  [key: string]: any;
}

interface User {
  name: string;
  id: string;
  companyId: string;
  isAdmin: boolean;
  [key: string]: any;
}

interface Ticket {
  id: string;
  requestedById: string;
  subject: string;
  assigneeId: string;
  status: 'Open' | 'In Progress' | 'Resolved' | 'Closed';
  priority: 'High' | 'Medium' | 'Low';
  createdDate: Timestamp;
  dueDate: Timestamp;
  isActive: boolean;
}

interface TicketWithNames extends Ticket {
  requestedByName: string;
  assigneeName: string;
}

const Tickets = () => {

  const { currentUser, isAdmin, isManager, isStaff } = useAuth();
  const isCollapsed = useAppSelector(state => state.sidebar.isCollapsed);
  const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState<TicketWithNames[]>([]);
  const [statusCounts, setStatusCounts] = useState({
    Open: 0,
    'In Progress': 0,
    Resolved: 0,
    Closed: 0
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<TicketWithNames | null>(null);
  const [companyUsers, setCompanyUsers] = useState<User[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isErrorToast, setIsErrorToast] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [filters, setFilters] = useState({
    status: [] as string[],
    priority: [] as string[],
    assigneeId: [] as string[]
  });
  const filterPopupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterPopupRef.current && !filterPopupRef.current.contains(event.target as Node)) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [currentUser]);

  const fetchCompanyUsers = async (companyId: string) => {
    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('companyId', '==', companyId)
      );
      const usersSnapshot = await getDocs(usersQuery);
      const users: User[] = [];
      usersSnapshot.forEach((doc) => {
        users.push({ id: doc.id, ...doc.data() } as User);
      });
      setCompanyUsers(users);
    } catch (error) {
      console.error('Error fetching company users:', error);
    }
  };

  const fetchTickets = async () => {
    if (!currentUser) return;
    setLoading(true);

    try {
      // Get the current user's data to check if they're an admin
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const userData = userDoc.data() as User | undefined;
      if (!userData) return;

      let ticketsQuery;
      if (userData.isAdmin && currentScreen === 'Admin') {
        // Admin sees all active tickets for their company
        ticketsQuery = query(
          collection(db, `companies/${userData.companyId}/tickets`),
          where('isActive', '==', true)
        );
        // Fetch company users for admin
        await fetchCompanyUsers(userData.companyId);
      } else if (userData.isManager && currentScreen === 'Manager') {

        // find all users that have the same managerId as the current user
        const usersQuery = query(
          collection(db, 'users'),
          where('companyId', '==', userData.companyId),
          where('managerId', '==', currentUser.uid)
        );
        const usersSnapshot = await getDocs(usersQuery);
        const users = usersSnapshot.docs.map(doc => doc.id);

        // Admin sees all active tickets for their company
        ticketsQuery = query(
          collection(db, `companies/${userData.companyId}/tickets`),
          where('assigneeId', 'in', users),
          where('isActive', '==', true)
        );
        // Fetch company users for admin
        await fetchCompanyUsers(userData.companyId);
      } else {
        // Regular user sees only tickets assigned to them
        ticketsQuery = query(
          collection(db, `companies/${userData.companyId}/tickets`),
          where('assigneeId', '==', currentUser.uid),
          where('isActive', '==', true)
        );
      }

      // Calculate date 6 months ago
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

      ticketsQuery = query(
        ticketsQuery,
        where('createdDate', '>=', Timestamp.fromDate(sixMonthsAgo))
      );

      const ticketsSnapshot = await getDocs(ticketsQuery);
      const ticketsData: Ticket[] = [];
      const counts = { Open: 0, 'In Progress': 0, Resolved: 0, Closed: 0 };

      for (const ticketDoc of ticketsSnapshot.docs) {
        const ticketData = ticketDoc.data();
        const ticket = {
          id: ticketDoc.id,
          ...ticketData
        } as Ticket;

        ticketsData.push(ticket);
        counts[ticket.status]++;
      }

      // Fetch user names for all tickets in parallel
      const ticketsWithNames = await Promise.all(
        ticketsData.map(async (ticket) => {
          // Fetch requestedBy user's name from leads collection
          const leadDoc = await getDoc(doc(db, `companies/${userData.companyId}/leads/${ticket.requestedById}`));
          const leadData = leadDoc.data() as Lead | undefined;

          // Fetch assignee's name from users collection
          const assigneeDoc = await getDoc(doc(db, `users/${ticket.assigneeId}`));
          const assigneeData = assigneeDoc.data() as User | undefined;

          return {
            ...ticket,
            requestedByName: leadData?.name || 'Unknown',
            assigneeName: assigneeData?.name || 'Unknown'
          };
        })
      );

      setTickets(ticketsWithNames);
      setStatusCounts(counts);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (ticketId: string) => {
    if (!currentUser) return;

    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const userData = userDoc.data() as User | undefined;
      if (!userData) return;

      const ticketRef = doc(db, `companies/${userData.companyId}/tickets`, ticketId);
      await updateDoc(ticketRef, {
        isActive: false
      });
      fetchTickets();
      setToastMessage('Ticket deleted successfully!');
      setIsErrorToast(false);
      setShowToast(true); // Refresh the list
    } catch (error) {
      setToastMessage('Error deleting ticket.Please try again.');
      setIsErrorToast(true);
      setShowToast(true);
      console.error('Error deleting ticket:', error);
    }
  };

  const handleFilterChange = (filterType: keyof typeof filters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter(item => item !== value)
        : [...prev[filterType], value]
    }));
  };

  const clearFilters = () => {
    setFilters({
      status: [],
      priority: [],
      assigneeId: []
    });
    setIsFilterOpen(false);
  };

  const applyFilters = () => {
    setIsFilterOpen(false);
  };

  const statusBoxes = [
    { status: 'Open', count: statusCounts.Open, color: 'border-blue-500', icon: 'tabler-progress-alert.svg' },
    { status: 'In Progress', count: statusCounts['In Progress'], color: 'border-yellow-500', icon: 'tabler_progress-check.svg' },
    { status: 'Resolved', count: statusCounts.Resolved, color: 'border-green-500', icon: 'teenyicons_folder-tick-outline.svg' },
    { status: 'Closed', count: statusCounts.Closed, color: 'border-red-500', icon: 'material-symbols_tab-close-outline.svg' }
  ];

  const TicketDetailModal = ({
    ticket,
    onClose,
    onSave,
    isAdmin
  }: {
    ticket: TicketWithNames | null;
    onClose: () => void;
    onSave: (updatedTicket: TicketWithNames) => Promise<void>;
    isAdmin: boolean;
  }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedTicket, setEditedTicket] = useState<TicketWithNames | null>(ticket);

    const handleInputChange = (field: keyof TicketWithNames, value: any) => {
      if (editedTicket) {
        setEditedTicket({ ...editedTicket, [field]: value });
      }
    };

    const handleSave = async () => {
      if (editedTicket) {
        await onSave(editedTicket);
        setIsEditing(false);
      }
    };

    if (!ticket || !editedTicket) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-3xl p-8 max-w-2xl w-full mx-4">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-montserrat text-gray-900">Ticket Details</h2>
            <div className="flex items-center gap-4">
              {!isEditing && (
                <img
                  src={`${process.env.PUBLIC_URL}/images/desktop/editicon.svg`}
                  alt="Edit"
                  className="w-5 h-5 cursor-pointer"
                  onClick={() => setIsEditing(true)}
                />
              )}
              <button onClick={onClose}>
                <img
                  src={`${process.env.PUBLIC_URL}/images/desktop/close.svg`}
                  alt="Close"
                  className="w-6 h-6"
                />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="font-montserrat text-sm text-gray-500 mb-1">Ticket ID</h3>
              <p className="font-montserrat text-gray-900">{editedTicket.id.slice(0, 8)}</p>
            </div>
            <div>
              <h3 className="font-montserrat text-sm text-gray-500 mb-1">Status</h3>
              {isEditing ? (
                <select
                  value={editedTicket.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full px-2 py-1 border rounded"
                >
                  <option value="Open">Open</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Closed">Closed</option>
                </select>
              ) : (
                <span className={`px-2 py-1 rounded-full ${editedTicket.status === 'Open' ? 'bg-blue-100 text-blue-800' :
                  editedTicket.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                    editedTicket.status === 'Resolved' ? 'bg-green-100 text-green-800' :
                      'bg-red-100 text-red-800'
                  }`}>
                  {editedTicket.status}
                </span>
              )}
            </div>
            <div>
              <h3 className="font-montserrat text-sm text-gray-500 mb-1">Requested By</h3>
              <p className="font-montserrat text-gray-900">{editedTicket.requestedByName}</p>
            </div>
            <div>
              <h3 className="font-montserrat text-sm text-gray-500 mb-1">Assignee</h3>
              {isEditing && isAdmin ? (
                <select
                  value={editedTicket.assigneeId}
                  onChange={(e) => handleInputChange('assigneeId', e.target.value)}
                  className="w-full px-2 py-1 border rounded"
                >
                  {companyUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="font-montserrat text-gray-900">{editedTicket.assigneeName}</p>
              )}
            </div>
            <div>
              <h3 className="font-montserrat text-sm text-gray-500 mb-1">Created On</h3>
              <p className="font-montserrat text-gray-900">
                {editedTicket.createdDate.toDate().toLocaleDateString()}
              </p>
            </div>
            <div>
              <h3 className="font-montserrat text-sm text-gray-500 mb-1">Due Date</h3>
              {isEditing ? (
                <input
                  type="date"
                  value={editedTicket.dueDate.toDate().toISOString().split('T')[0]}
                  onChange={(e) => handleInputChange('dueDate', Timestamp.fromDate(new Date(e.target.value)))}
                  className="w-full px-2 py-1 border rounded"
                />
              ) : (
                <p className="font-montserrat text-gray-900">
                  {editedTicket.dueDate.toDate().toLocaleDateString()}
                </p>
              )}
            </div>
            <div>
              <h3 className="font-montserrat text-sm text-gray-500 mb-1">Priority</h3>
              {isEditing ? (
                <select
                  value={editedTicket.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  className="w-full px-2 py-1 border rounded"
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              ) : (
                <span className={`px-2 py-1 rounded-full ${editedTicket.priority === 'High' ? 'bg-red-100 text-red-800' :
                  editedTicket.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                  {editedTicket.priority}
                </span>
              )}
            </div>
            <div className="col-span-2">
              <h3 className="font-montserrat text-sm text-gray-500 mb-1">Subject</h3>
              <p className="font-montserrat text-gray-900 whitespace-pre-wrap">{editedTicket.subject}</p>
            </div>
          </div>

          {isEditing && (
            <div className="flex justify-end mt-6 gap-4">
              <button
                onClick={() => {
                  setEditedTicket(ticket);
                  setIsEditing(false);
                }}
                className="px-4 py-2 text-white bg-black rounded-full text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-white bg-cyan-600 rounded-full text-sm"
              >
                Save Changes
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Filter tickets based on search query and filters
  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.subject.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filters.status.length === 0 || filters.status.includes(ticket.status);
    const matchesPriority = filters.priority.length === 0 || filters.priority.includes(ticket.priority);
    const matchesAssignee = filters.assigneeId.length === 0 || filters.assigneeId.includes(ticket.assigneeId);

    return matchesSearch && matchesStatus && matchesPriority && matchesAssignee;
  });

  return (
    <div className="min-h-screen bg-gray-200">
      {/* <div className="fixed top-0 left-0 w-full z-10">
        <TopNavbar />
      </div> */}
      <div className="fixed top-16 left-0 h-full z-10">
        <SideNavbar />
      </div>

      <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
        <div className="bg-white p-6 rounded-3xl shadow-md w-full">
          <div className="flex items-center gap-6 mb-6">
            <div
              className="flex items-center justify-center w-14 h-14 rounded-full"
              style={{ background: 'linear-gradient(180deg, #00A6D9 10%, #3483BE 100%)' }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/desktop/tickets.svg`}
                alt="Ticket Icon"
                className="w-8 h-8"
              />
            </div>
            <h2 className="font-montserrat text-2xl font-medium-gray-900">Tickets</h2>
          </div>
          <div className="flex gap-12 mb-8 w-2/3">
            {statusBoxes.map((item, index) => (
              <div
                key={index}
                className="flex-1 bg-gray-100 rounded-3xl p-2 shadow-md h-20"
                style={{
                  borderBottom: `4px solid transparent`,
                  borderImage: `linear-gradient(to right, transparent 20%,  ${item.color} 50%, transparent 80%) 1`
                }}
              >
                <div className="flex flex-col h-full">
                  <div className="flex justify-center mb-1">
                    <img
                      src={`${process.env.PUBLIC_URL}/images/desktop/${item.icon}`}
                      alt={`${item.status} Icon`}
                      className="w-6 h-6"
                    />
                  </div>
                  <div className="flex justify-between items-end mt-auto">
                    <span className="text-lg font-Montserrat-medium text-gray-900">{item.count}</span>
                    <span className="text-xs font-Montserrat-medium text-gray-600">{item.status}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <h3 className="font-montserrat text-xl font-medium-gray-900 mb-6">Manage Tickets</h3>
          <p className="text-sm text-gray-500 mb-6">* Showing tickets from the last 6 months only</p>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center rounded-3xl bg-zinc-100 w-80">
              <img
                src={`${process.env.PUBLIC_URL}/images/desktop/searchbg.svg`}
                alt="Search Icon"
                className="w-5 h-5 ml-4"
              />
              <input
                type="text"
                placeholder="Search tickets..."
                className="px-4 py-2 text-gray-700 bg-zinc-100 focus:outline-none rounded-3xl w-full"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="relative">
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="text-gray-600 hover:text-gray-800"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/desktop/NavFilter.svg`}
                  alt="filters-icon"
                  className="w-[35px] h-[35px]"
                />
              </button>

              {isFilterOpen && (
                <div
                  ref={filterPopupRef}
                  className="absolute top-0 right-0 transform translate-x-3 w-[261px] bg-white border rounded-3xl shadow-lg p-4 z-50"
                >
                  <div className="flex justify-between items-center">
                    <h3 className="text-[14px] font-normal leading-[20px] text-left">Filters</h3>
                    <img
                      src={`${process.env.PUBLIC_URL}/images/desktop/black-filter.svg`}
                      alt="filters-black-icon"
                      onClick={() => setIsFilterOpen(false)}
                      className="w-9 h-9 cursor-pointer"
                    />
                  </div>

                  <div className="mt-4 space-y-4">
                    <div>
                      <span className="text-[12px] font-normal leading-[16px]">Status:</span>
                      <div className="mt-2 space-y-1">
                        {['Open', 'In Progress', 'Resolved', 'Closed'].map(status => (
                          <label key={status} className="flex items-center space-x-2 cursor-pointer">
                            <div className="relative">
                              <input
                                type="checkbox"
                                checked={filters.status.includes(status)}
                                onChange={() => handleFilterChange('status', status)}
                                className="peer appearance-none h-5 w-5 border-2 border-gray-300 rounded-sm cursor-pointer checked:border-orange-500 checked:bg-white hover:border-orange-500 transition-colors"
                              />
                              <div className="absolute top-1/2 left-1/2 transform -translate-x-1 -translate-y-1.5 opacity-0 peer-checked:opacity-100 pointer-events-none z-10">
                                <div className="w-2 h-2 rounded-sm bg-orange-500"></div>
                              </div>
                            </div>
                            <span className="text-[12px]">{status}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <span className="text-[12px] font-normal leading-[16px]">Priority:</span>
                      <div className="mt-2 space-y-1">
                        {['High', 'Medium', 'Low'].map(priority => (
                          <label key={priority} className="flex items-center space-x-2 cursor-pointer">
                            <div className="relative">
                              <input
                                type="checkbox"
                                checked={filters.priority.includes(priority)}
                                onChange={() => handleFilterChange('priority', priority)}
                                className="peer appearance-none h-5 w-5 border-2 border-gray-300 rounded-sm cursor-pointer checked:border-orange-500 checked:bg-white hover:border-orange-500 transition-colors"
                              />
                              <div className="absolute top-1/2 left-1/2 transform -translate-x-1 -translate-y-1.5 opacity-0 peer-checked:opacity-100 pointer-events-none z-10">
                                <div className="w-2 h-2 rounded-sm bg-orange-500"></div>
                              </div>
                            </div>
                            <span className="text-[12px]">{priority}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {isAdmin && (
                      <div>
                        <span className="text-[12px] font-normal leading-[16px]">Assignee:</span>
                        <div className="mt-2">
                          <div className="relative">
                            <select
                              multiple
                              value={filters.assigneeId}
                              onChange={(e) => {
                                const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                                setFilters(prev => ({
                                  ...prev,
                                  assigneeId: selectedOptions
                                }));
                              }}
                              className="w-full p-2 text-sm border rounded-lg focus:outline-none focus:border-orange-500"
                              size={4}
                            >
                              {companyUsers.map(user => (
                                <option key={user.id} value={user.id}>
                                  {user.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between space-x-3 mt-4">
                    <button
                      onClick={clearFilters}
                      className="px-8 py-2 text-white bg-black rounded-full text-sm"
                    >
                      Clear
                    </button>
                    <button
                      onClick={applyFilters}
                      className="px-8 py-2 text-white bg-cyan-600 rounded-full text-sm"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="rounded-xl overflow-hidden bg-white shadow-lg">
            {loading ? (
              <TableSkeleton columns={8} rows={5} />
            ) : (
            <div className="overflow-x-auto relative">
              <table className="min-w-full table-auto border-collapse">
                <thead>
                  <tr className="bg-gradient-to-r from-[#02385E] to-[#02385E]/90">
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat w-[100px]">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat w-[180px]">Requested By</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat w-[180px]">Assignee</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat w-[140px]">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat w-[120px]">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat w-[120px]">Created Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat w-[120px]">Due Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat w-[120px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTickets.map((ticket, index) => (
                    <tr key={ticket.id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat w-[100px]">
                        <div className="truncate text-left" title={ticket.id}>{ticket.id.slice(0, 8)}</div>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat w-[180px]">
                        <div className="truncate text-left" title={ticket.requestedByName}>{ticket.requestedByName}</div>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat w-[180px]">
                        <div className="truncate text-left" title={ticket.assigneeName}>{ticket.assigneeName}</div>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat w-[140px]">
                        <span className={`inline-block min-w-[100px] px-2 py-1 rounded-full ${ticket.status === 'Open' ? 'bg-blue-100 text-blue-800' :
                          ticket.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                            ticket.status === 'Resolved' ? 'bg-green-100 text-green-800' :
                              'bg-red-100 text-red-800'
                          }`}>
                          {ticket.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat w-[120px]">
                        <span className={`inline-block min-w-[80px] px-2 py-1 rounded-full ${ticket.priority === 'High' ? 'bg-red-100 text-red-800' :
                          ticket.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                          {ticket.priority}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat w-[120px]">
                        <div className="truncate text-left" title={ticket.createdDate.toDate().toLocaleDateString()}>
                          {ticket.createdDate.toDate().toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat w-[120px]">
                        <div className="truncate text-left" title={ticket.dueDate.toDate().toLocaleDateString()}>
                          {ticket.dueDate.toDate().toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat w-[120px]">
                        <div className="flex gap-4">
                          <img
                            src={`${process.env.PUBLIC_URL}/images/desktop/lucide-view.svg`}
                            alt="View Details"
                            className="w-5 h-5 cursor-pointer"
                            onClick={() => setSelectedTicket(ticket)}
                          />
                          <img
                            src={`${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg`}
                            alt="Delete Icon"
                            className="w-5 h-5 cursor-pointer"
                            onClick={() => handleDelete(ticket.id)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        </div>
      </div>
      {selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
          onSave={async (updatedTicket) => {
            if (!currentUser) return;

            try {
              const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
              const userData = userDoc.data() as User | undefined;
              if (!userData) return;

              const ticketRef = doc(db, `companies/${userData.companyId}/tickets`, updatedTicket.id);
              await updateDoc(ticketRef, {
                assigneeId: updatedTicket.assigneeId,
                status: updatedTicket.status,
                priority: updatedTicket.priority,
                dueDate: updatedTicket.dueDate
              });
              setSelectedTicket(null);
              fetchTickets();
            } catch (error) {
              console.error('Error updating ticket:', error);
            }
          }}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
};

export default Tickets;